import React, {Component} from 'react';
import DateRangeFilter from '../components/DateRangeFilter';
import SecondaryFilter from '../components/SecondaryFilter';
import DiscountsReportSet from '../components/DiscountsReportSet';
import ReportHeaderState from '../components/ReportHeaderState';
import NoAccess from '../noAccess';
import {RESOURCES as filterResources} from '../fuseFiltersResourceMap';
import {getTimeZoneOffsetDefault} from '../shared/dateTimeLists';
import {hasReportsAccess} from '../shared/authFunctions';
import {ViewTitle} from 'admin-on-rest/lib/mui';
import {Card, CardText} from 'material-ui/Card';
import Avatar from 'material-ui/Avatar';
import Icon from 'material-ui/svg-icons/editor/monetization-on';

export const fuseDiscountsReportsIcon = Icon;

/**
 * Discounts Reports class for retrieving and displaying summary
 *
 */
export class DiscountsReports extends Component {
    constructor(props) {
        super(props);
        const system = localStorage.getItem('system');
        const offsetDefault = getTimeZoneOffsetDefault();
        this.state = {
            dataProcessed: false,
            errorMessage: null,
            filterChoiceFirst: null,
            filterChoiceSecond: null,
            filterType: 'all',
            filterTypeName: null,
            location: {
                id: null,
                name: '',
                city: '',
                state: ''
            },
            selectedEndDate: null,
            selectedStartDate: null,
            selectedOffset: offsetDefault,
            summaryComplete: false,
            system: system,
            totalFiltersAvailable: 0,
        };
    }

    /**
     * Handle event props for initial dataset processing
     *
     */
    handleDataProcessed = () => {
        this.setState({
            dataProcessed: true
        });
    };

    /**
     * Convert Dates to visual display
     *
     * @param selectedStartDate
     * @param selectedEndDate
     * @return {string}
     */
    displaySelectedDates(selectedStartDate, selectedEndDate) {
        const options = {year: 'numeric', month: 'long', day: 'numeric'};
        let startDate = new Date(selectedStartDate + 'T00:00:00');
        let endDate = new Date(selectedEndDate + 'T00:00:00');
        let startDateStr = startDate.toLocaleDateString('en-US', options);
        let endDateStr = endDate.toLocaleDateString('en-US', options);

        return (
            'Date Range: ' + startDateStr + ' through ' + endDateStr
        )
    }

    /**
     * Handle event props from DateRangeFilter
     *
     * @param type
     * @param value
     */
    handleDateRangeFilterSelect = (type, value) => {
        switch (type) {
            case 'start_date':
                // Set Start Date selection for reports summary component
                this.setState({selectedStartDate: value});
                break;
            case 'end_date':
                // Set End selection for reports summary component
                this.setState({selectedEndDate: value});
                break;
            case 'offset':
                // Set Time Zone Offset selection for reports summary component
                this.setState({selectedOffset: value});
                break;
            default:
                return
        }
    };

    /**
     * Handle event for Filter type selection
     *
     * @param type
     */
    handleFilterTypeSelect = (type) => {
        this.setState({
            filterChoiceFirst: null,
            filterChoiceSecond: null,
            filterType: type.id,
            filterTypeName: type.name,
            totalFiltersAvailable: 0
        });
    };

    /**
     * Handle event for Secondary Filter selection(s)
     *
     * @param firstChoice
     * @param secondChoice
     */
    handleFilterOptionSelect = (firstChoice, secondChoice) => {
        let filterChoiceFirst = null;
        if (firstChoice) {
            filterChoiceFirst = firstChoice;
        }
        if (secondChoice === 'none') {
            this.setState({
                filterChoiceFirst: filterChoiceFirst,
                filterChoiceSecond: null,
                totalFiltersAvailable: 1
            });
        } else {
            this.setState({
                filterChoiceFirst: filterChoiceFirst,
                filterChoiceSecond: secondChoice,
                totalFiltersAvailable: 2
            });
        }
    };

    render() {
        const {
            filterChoiceFirst,
            filterChoiceSecond,
            filterType,
            filterTypeName,
            selectedEndDate,
            selectedOffset,
            selectedStartDate,
            system,
            totalFiltersAvailable
        } = this.state;

        if (!hasReportsAccess()) return <NoAccess/>;

        const ListTitle = <span className="list-title hide-print">
            <Avatar icon={<Icon/>} className="avatar-title"/>Discounts Report</span>;

        const ListDescription = () => {
            return (
                <span className="list-description hide-print">
                    <p>Discounts Report displays total discounts used and $ amount discounted by location. It also lists by discount the number used and $ amount discounted. It also lists VIP Customers who have used discounts and amount they saved.</p>
                    <p>Select a Date Range and Time Zone Offset to generate a Report. You may apply additional filters
                        to further refine results.</p>
                </span>
            );
        };

        let showFilterPrompt = false;
        let showLoading = !!(selectedStartDate && selectedEndDate && selectedOffset);
        if (filterType && filterType !== 'all') {
            if (totalFiltersAvailable === 2 && (!filterChoiceFirst || !filterChoiceSecond)) {
                showFilterPrompt = true;
                showLoading = false;
            } else if (!filterChoiceFirst) {
                showFilterPrompt = true;
                showLoading = false;
            }
        }
        let filterPromptName = (filterTypeName) ? ' for ' + filterTypeName.substr(3) : '';

        return (
            <div className="lists-container">
                <Card>
                    <ViewTitle title={ListTitle}/>
                    <CardText>
                        <ListDescription/>

                        {/* Report Filter (Start Date, End Date, Time Zone) */}
                        <DateRangeFilter
                            onSelectStartDate={this.handleDateRangeFilterSelect}
                            onSelectEndDate={this.handleDateRangeFilterSelect}
                            onSelectOffset={this.handleDateRangeFilterSelect}
                        />

                        {/* Secondary Filters */}
                        {selectedStartDate && selectedEndDate && selectedOffset &&
                        <SecondaryFilter
                            startDate={selectedStartDate}
                            endDate={selectedEndDate}
                            offset={selectedOffset}
                            resource={filterResources.DiscountFilterByItems}
                            onFilterTypeSelect={this.handleFilterTypeSelect}
                            onFilterChoiceSelect={this.handleFilterOptionSelect}
                        />
                        }

                        {/* Print Header */}
                        {selectedStartDate && selectedEndDate &&
                        <ReportHeaderState
                            reportType="Discounts Report Summary"
                        />
                        }

                        {/* Prompt Messaging */}
                        {showFilterPrompt &&
                        <div className="loader-container">
                            <h4>Please complete a Secondary Filter{filterPromptName}.</h4>
                        </div>
                        }

                        {/* Discounts Report Content */}
                        {selectedStartDate && selectedEndDate && selectedOffset &&
                        <div>
                            <DiscountsReportSet
                                system={system}
                                startDate={selectedStartDate}
                                endDate={selectedEndDate}
                                offset={selectedOffset}
                                filterType={filterType}
                                filterChoiceFirst={filterChoiceFirst}
                                filterChoiceSecond={filterChoiceSecond}
                                totalFiltersAvailable={totalFiltersAvailable}
                                showLoading={showLoading}
                                onDataProcessed={this.handleDataProcessed}
                            />
                        </div>
                        }
                    </CardText>
                </Card>
            </div>
        )
    }
}



// WEBPACK FOOTER //
// ./src/Fuse/Report/DiscountsReports.js